import EditIcon from '@mui/icons-material/Edit'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import { useCarriersView } from 'context/CarriersViewContext'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CARRIERS_QUERY_KEY } from './CarrierManager.service'
import { type CarrierData, IntegrationType, TelematicsType } from './CarrierManager.types'
import EditCarrierDialog from './EditCarrierDialog'

interface CarrierTableProps {
  carriers: CarrierData[]
  pageCount?: number
}

const CarrierTable = ({
  carriers,
  pageCount,
}: CarrierTableProps): ReactElement => {
  const { t } = useTranslation()
  const { page, setPage } = useCarriersView()
  const [activeCarrier, setActiveCarrier] = useState<CarrierData>()
  const editCarrierDialog = useDialog<typeof EditCarrierDialog>()
  const isFetching = useIsFetching({ queryKey: [CARRIERS_QUERY_KEY] })

  const handleEditClick = useCallback((row: CarrierData) => {
    setActiveCarrier(row)
    editCarrierDialog.handleOpen()
  }, [])

  const nameColumnValue = (row: CarrierData) => (
    <Typography variant="p3" color="text.main">{row.name}</Typography>
  )

  const customerIdentifierColumnValue = (row: CarrierData) => (
    <Typography variant="p3" color="text.main" component="p" noWrap>{row.customerIdentifier}</Typography>
  )

  const integrationColumnValue = (row: CarrierData) => {
    if (row.integration === IntegrationType.NONE) {
      return <Typography variant="p5" color="primary.light">{t('general.none')}</Typography>
    }
    return t(`screens.cs.carrier_manager.integrations.${row.integration}`)
  }

  const telematicsColumnValue = (row: CarrierData) => {
    if (row.telematics === TelematicsType.NONE) {
      return <Typography variant="p5" color="primary.light">{t('general.none')}</Typography>
    }
    return t(`screens.cs.carrier_manager.telematics.${row.telematics}`)
  }

  const featuresColumnValue = (row: CarrierData) => {
    if (!row.recommendedRoutes && !row.automaticWaypointUpdatesAllowed) {
      return <Typography variant="p5" color="primary.light">{t('general.none')}</Typography>
    }
    const features: string[] = []
    if (row.recommendedRoutes) {
      features.push(t(`screens.cs.carrier_manager.add_edit_carrier.shortest_routes`))
    }
    if (row.automaticWaypointUpdatesAllowed) {
      features.push(t(`screens.cs.carrier_manager.add_edit_carrier.status_automation`))
    }
    return features.toString()
  }

  const columns = useMemo((): KNDataTableColumn<CarrierData>[] => [
    {
      name: 'name',
      label: t('screens.cs.carrier_manager.card.columns.name'),
      getValue: nameColumnValue,
      sx: { width: { xs: '24rem', xxl: 'auto' } },
    },
    {
      name: 'customerIdentifier',
      label: t('screens.cs.carrier_manager.card.columns.customer_identifier'),
      getValue: customerIdentifierColumnValue,
      sx: { width: '12rem' },
    },
    {
      name: 'integration',
      label: t('screens.cs.carrier_manager.card.columns.integration'),
      getValue: integrationColumnValue,
      sx: { width: '12rem' },
    },
    {
      name: 'telematics',
      label: t('screens.cs.carrier_manager.card.columns.telematics'),
      getValue: telematicsColumnValue,
      sx: { width: '12rem' },
    },
    {
      name: 'features',
      label: t('screens.cs.carrier_manager.card.columns.features'),
      getValue: featuresColumnValue,
      sx: { width: '14rem' },
    },
  ], [])

  const actions = useMemo((): KNDataTableAction<CarrierData>[] => [
    {
      name: 'edit',
      label: t('screens.cs.carrier_manager.card.actions.edit_short'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={actions}
        data={carriers}
        page={page}
        pageCount={pageCount}
        onPageChange={setPage}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
        }}
      />
      {activeCarrier && (
        <EditCarrierDialog
          payload={{
            carrier: activeCarrier,
          }}
          open={editCarrierDialog.isOpen}
          onAction={editCarrierDialog.handleAction}
          onClose={editCarrierDialog.handleClose}
        />
      )}
    </>
  )
}

export default CarrierTable
